/**
 * NotificationBell — UAT-3A header bell + unread badge.
 *
 * Owns the `useNotifications` controller and shares it with the drawer
 * so the badge and the list stay in lockstep across opens/closes.
 *
 * Visible only when authenticated. Click opens the drawer.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Icon } from '../common/Icon';
import useNotifications from '../../hooks/useNotifications';
import { NotificationDrawer } from './NotificationDrawer';

export function NotificationBell({ enabled = true }) {
  const { t } = useTranslation('notifications');
  const [open, setOpen] = useState(false);
  const controller = useNotifications(enabled);
  const { unread } = controller;

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="relative h-9 w-9"
        onClick={() => setOpen(true)}
        aria-label={t('bell.ariaLabel')}
        data-testid="notification-bell"
      >
        <Icon name="bell" className="h-5 w-5" />
        {unread > 0 ? (
          <span
            className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-unread-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </Button>

      <NotificationDrawer
        open={open}
        onOpenChange={setOpen}
        controller={controller}
      />
    </>
  );
}

export default NotificationBell;
